// biome-ignore-all lint/correctness/useUniqueElementIds: historic

import { useQuery } from '@apollo/client/react';
import FinishSetupBanner from '@components/FinishSetupBanner/FinishSetupBanner';
import Layout from '@components/Layout/Layout';
import SelectorBar, { SelectorBarItem } from '@components/SelectorBar/SelectorBar';
import Tabs, { Tab } from '@components/Tabs/Tabs';
import { WelcomeAppExperience } from '@components/WelcomeAppExperience/WelcomeAppExperience';
import { useUser } from '@context/user-context';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { useCard } from '@hooks/card';
import useLazyWorkspaceUrl from '@hooks/use-lazy-workspace-url';
import useNode from '@hooks/use-node';
import useWorkspaceID from '@hooks/use-workspace-id';
import { analytics, helpers } from '@modules/analytics';
import { editPersonalCard } from '@modules/identity/editPersonalCard';
import { CardHeaderNav } from 'app-next/[workspace]/cards/components/CardHeaderNav/CardHeaderNav';
import { DeleteCardWarningDialog } from 'app-next/[workspace]/cards/components/DeleteCardWarningDialog/DeleteCardWarningDialog';
import Router, { useRouter } from 'next/router';
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { ShareCardPopover } from '../../../components/ShareSheets/ShareCardPopover';
import { ActorCardsAndProfilesDocument } from './actor-cards-profiles.graphql';
import { SelectorBarAddNewPersonalCardButton } from './components/SelectorBar';
import styles from './layout.module.scss';

interface LayoutProps {
  children: ReactNode;
}

const getDefaultCardTitle = (index: number) =>
  `Card ${index.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  })}`;

export default function PersonalCardPreviewLayout({ children }: LayoutProps): JSX.Element {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { user } = useUser();

  const router = useRouter();

  const { data: actorData } = useQuery(ActorCardsAndProfilesDocument, {
    variables: {
      timezone,
    },
    fetchPolicy: 'cache-and-network',
  });

  const generateWorkspaceUrl = useLazyWorkspaceUrl();

  // Dialog flags
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareCardPopover, setShowShareCardPopover] = useState(false);

  const [shareCardBtnNode, shareCardBtnRef] = useNode<HTMLDivElement>();

  const activeTab = router.query.tab ?? 'card';

  const currentCardId = useMemo(() => {
    return router.query.cardId as string;
  }, [router.query.cardId]);

  const { data: currentCard } = useCard(currentCardId);

  const { maxPersonalCards } = useActorPlan();
  const workspaceId = useWorkspaceID();

  // The `/cards/personal` list also surfaces the viewer's TEAM cards; the edit
  // route only sends a card into the individual identity editor when it belongs
  // to the current workspace, so pass its workspace as the routing hint.
  const currentCardWorkspaceId = actorData?.actor?.cards?.edges?.find(
    (edge) => edge?.node?.id === currentCardId,
  )?.node?.profile?.workspace?.id;

  const handleEditCard = async () => {
    const url = await editPersonalCard({
      cardId: currentCardId,
      cardWorkspaceId: currentCardWorkspaceId ?? undefined,
      returnTo: `/${workspaceId}/cards/personal/${currentCardId}/preview`,
    });
    await router.push(url);
  };

  const handleTabClick = useCallback((id: string) => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    params.set('tab', id);

    Router.push(`${location.pathname}?${params}`, undefined, { shallow: true });
  }, []);

  const personalCards = actorData?.actor?.cards?.edges
    ?.map((edge) => edge.node)
    ?.sort((card1, card2) => {
      // if workspace type of the cards are the same, compare them by created_at dates
      if (card1?.profile?.workspace?.type === card2?.profile?.workspace?.type) {
        const date1 = new Date(card1?.created_at).getTime();
        const date2 = new Date(card2?.created_at).getTime();

        // this makes the newest TEAM card show up first in the array, and the newest INDIVIDUAL card show up last in the array, which is the current experience
        return card1?.profile?.workspace?.type === 'TEAM' ? date2 - date1 : date1 - date2;
      }

      // if workspace type of the cards are different, sort by card type
      // TEAM cards are returned first, INDIVIDUAL cards are returned last
      return card1?.profile?.workspace?.type === 'TEAM' ? -1 : 1;
    });

  // this will hide the edit button for all extra personal cards that exceed the maximum number user is allowed
  const showEditButton = useMemo(
    () =>
      !personalCards?.some(
        (card, index) => index + 1 > maxPersonalCards && card?.id === currentCardId,
      ),
    [currentCardId, personalCards, maxPersonalCards],
  );

  const appDownloaded = user?.uiEvents?.appDownloaded || user?.isIosDevice !== undefined;
  const showFinishSetupBanner = !appDownloaded;

  const handleCardShare = () => {
    analytics.track({
      name: 'sharecardbutton_clicked',
      properties: {
        ...helpers.mapGraphQLCardPropertiesToAnalyticsProperties(currentCard?.card),
      },
    });

    setShowShareCardPopover(true);
  };

  return (
    <Layout title="Personal Cards">
      <CardHeaderNav
        showEditButton={showEditButton}
        showDeleteButton={true}
        showOptionButtons={true}
        showShareButton={true}
        shareCardBtnRef={shareCardBtnRef}
        onClickDelete={() => setShowDeleteDialog(true)}
        onClickEdit={() => {
          void handleEditCard();
        }}
        onClickSettings={() => setShowSettingsDialog(true)}
        onClickShare={handleCardShare}
        onSyncUpdatesChange={() => setShowSettingsDialog(true)}
      />
      <div className={styles['card-personal-page']}>
        <SelectorBar
          title="Cards"
          addNewButton={<SelectorBarAddNewPersonalCardButton id="add-personal-card-btn" />}
          mode="card"
        >
          {personalCards?.map((card, index) => {
            if (!card) {
              return null;
            }

            const cardTitle = card?.properties?.title?.value || getDefaultCardTitle(index + 1);

            const exceedsMaxCardNumber = index + 1 > maxPersonalCards;

            // show lock icon if they have more than their max number of personal cards, this is controlled by isActive
            if (exceedsMaxCardNumber) {
              return (
                <SelectorBarItem
                  key={card.id}
                  name={cardTitle}
                  href={generateWorkspaceUrl(`cards/personal/${card.id}/preview`)}
                  selected={currentCardId === card.id}
                  isActive={false}
                />
              );
            }

            return (
              <SelectorBarItem
                key={card.id}
                name={cardTitle}
                href={generateWorkspaceUrl(`cards/personal/${card.id}/preview`)}
                selected={currentCardId === card.id}
              />
            );
          })}
        </SelectorBar>

        <div className={styles['main-content']} data-page-mode={'view'}>
          <div className={styles['tabs-container']}>
            <Tabs onClick={handleTabClick}>
              <Tab id="card" name="Card View" selected={activeTab === 'card'} />
              <Tab id="qr-code" name="QR Code" selected={activeTab === 'qr-code'} />
            </Tabs>
          </div>
          {children}
        </div>
        {showFinishSetupBanner && (
          <div className={styles['finish-setup-desktop']}>
            <FinishSetupBanner desktop={true} />
          </div>
        )}
      </div>

      <DeleteCardWarningDialog
        cardId={currentCardId}
        route="personal"
        show={showDeleteDialog}
        close={() => setShowDeleteDialog(false)}
      />

      <ShareCardPopover
        show={showShareCardPopover}
        closePopover={() => setShowShareCardPopover(false)}
        shareCardBtnNode={shareCardBtnNode}
        card={currentCard?.card}
      />

      {showFinishSetupBanner && (
        <div className={styles['finish-setup-tablet']}>
          <FinishSetupBanner desktop={false} />
        </div>
      )}

      <WelcomeAppExperience />
    </Layout>
  );
}
